import React from 'react';
import styled from 'styled-components';
import { } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  align-items: flex-start;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const BenefitNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #004744;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  margin-right: 1.5rem;
  flex-shrink: 0;
`;

const BenefitContent = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #004744;
  font-style: italic;
`;

const BenefitDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const InsuranceOptions = styled.div`
  margin: 5rem 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const OptionIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #004744;
  }
`;

const OptionTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #004744;
  font-style: italic;
`;

const OptionDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #004744;
  font-style: italic;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Insurance: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const insuranceBenefits = [
    {
      number: 1,
      title: language === 'it' ? 'COPERTURA PER DANNI E FURTI' : 'DAMAGE AND THEFT COVERAGE',
      description: language === 'it' 
        ? 'Copertura per danni alla proprietà e furti. La protezione può estendersi anche ai mobili e ad altri beni presenti nell\'immobile.'
        : 'Coverage for property damage and theft. Protection can also extend to furniture and other assets in the property.'
    },
    {
      number: 2,
      title: language === 'it' ? 'RESPONSABILITÀ CIVILE' : 'LIABILITY',
      description: language === 'it' 
        ? 'Copertura per lesioni fisiche o danni a terzi durante il soggiorno degli ospiti.'
        : 'Coverage for bodily injury or property damage to third parties during guests\' stay.'
    },
    {
      number: 3,
      title: language === 'it' ? 'ASSISTENZA LEGALE' : 'LEGAL ASSISTANCE',
      description: language === 'it' 
        ? 'Supporto e spese legali in caso di controversie o azioni legali con ospiti o terzi.'
        : 'Support and legal expenses in case of disputes or legal actions with guests or third parties.'
    },
    {
      number: 4,
      title: language === 'it' ? 'ASSICURAZIONE CONTRO INQUILINI NON PAGANTI' : 'NON-PAYING TENANT INSURANCE',
      description: language === 'it' 
        ? 'Coperture disponibili contro mancati pagamenti o danni causati dagli inquilini.'
        : 'Coverage available against missed rent payments or damage caused by tenants.'
    },
    {
      number: 5,
      title: language === 'it' ? 'PERSONALIZZAZIONE DELLE POLIZZE' : 'CUSTOMIZATION OF POLICIES',
      description: language === 'it' 
        ? 'Pacchetti configurabili sulle esigenze specifiche della proprietà e dell\'attività di locazione.'
        : 'Packages configurable to the specific needs of the property and rental activity.'
    }
  ];
  
  const insuranceOptions = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        </svg>
      ),
      title: language === 'it' ? 'ASSICURAZIONE BASE' : 'BASIC INSURANCE',
      description: language === 'it' 
        ? 'Copertura essenziale per danni all\'immobile e responsabilità civile; ideale per iniziare.'
        : 'Essential coverage for property damage and liability; ideal to get started.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="3" y1="9" x2="21" y2="9"></line>
          <line x1="9" y1="21" x2="9" y2="9"></line>
        </svg>
      ),
      title: language === 'it' ? 'ASSICURAZIONE PREMIUM' : 'PREMIUM INSURANCE',
      description: language === 'it' 
        ? 'Copertura dedicata per arredi, opere d’arte e oggetti di valore (furto, danneggiamento, eventi accidentali) con massimali dedicati e perizia su richiesta.'
        : 'Dedicated coverage for furniture, artwork and valuable items (theft, damage, accidental events) with dedicated limits and appraisal on request.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      ),
      title: language === 'it' ? 'CONSULENZA PERSONALIZZATA' : 'PERSONALIZED CONSULTATION',
      description: language === 'it' 
        ? 'Analisi delle tue esigenze e creazione di un pacchetto assicurativo su misura.'
        : 'We analyze your needs and craft a tailored insurance package.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Assicurazione | J-O-H' : 'Insurance | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Soluzioni assicurative su misura per la protezione degli immobili in affitto breve e della tua attività di hosting' 
            : 'Tailored insurance solutions for the protection of short-term rental properties and your hosting business'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'ASSICURAZIONE' : 'INSURANCE'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'L\'assicurazione per affitti brevi protegge immobile, ospiti e attività con coperture mirate ai rischi reali del settore.'
              : 'Short-term rental insurance protects property, guests and business with cover focused on the real risks of the sector.'}
          </Description>
          
          <BenefitsGrid>
            {insuranceBenefits.map((benefit) => (
              <BenefitCard key={benefit.number}>
                <BenefitNumber>{benefit.number}</BenefitNumber>
                <BenefitContent>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitDescription>{benefit.description}</BenefitDescription>
                </BenefitContent>
              </BenefitCard>
            ))}
          </BenefitsGrid>
          
          <SectionTitle>
            {language === 'it' ? 'LE NOSTRE OPZIONI ASSICURATIVE' : 'OUR INSURANCE OPTIONS'}
          </SectionTitle>
          
          <InsuranceOptions>
            {insuranceOptions.map((option, index) => (
              <OptionCard key={index}>
                <OptionIcon>
                  {option.icon}
                </OptionIcon>
                <OptionTitle>{option.title}</OptionTitle>
                <OptionDescription>{option.description}</OptionDescription>
              </OptionCard>
            ))}
          </InsuranceOptions>
          
          <Description>
            {language === 'it' 
              ? 'Integrare una copertura adeguata nella gestione degli affitti brevi ti offre serenità operativa e protezione finanziaria.'
              : 'Embedding proper coverage in short‑term rental operations provides peace of mind and financial protection.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Richiedi un Preventivo' : 'Request a Quote'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Insurance; 